import React, { useState } from "react";
import styled from "styled-components";
import { FiGift, FiX } from "react-icons/fi";
import toast from "react-hot-toast";
import APIRequest from "../../API";
import * as PATH from "../../API/path";
import {
  FANXOBILLPHONENUMBER,
  loadScript,
} from "../../Config/common.config";
import { ctaGradient } from "../../Components/sharedModalStyles";

// Fan-facing prices include the 20% platform charge: ₹120 paid credits ₹100.
const GIFT_PRESETS = [120, 600, 1200, 2400, 6000];

interface GiftButtonProps {
  creatorId?: string;
  creatorName?: string;
  channelSlug?: string;
  canSendGift: boolean;
  onGiftSent: (message: any) => void;
}

const GiftButton: React.FC<GiftButtonProps> = ({
  creatorId,
  creatorName,
  channelSlug,
  canSendGift,
  onGiftSent,
}) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(GIFT_PRESETS[0]);
  const [isProcessing, setIsProcessing] = useState(false);

  const numericAmount = amount;
  const isValidAmount = GIFT_PRESETS.includes(numericAmount);

  if (!canSendGift || !creatorId || !channelSlug) return null;

  const closeModal = () => {
    if (isProcessing) return;
    setOpen(false);
  };

  const verifyAndSendGift = async (paymentResponse: any) => {
    const verificationResponse: any = await APIRequest(PATH.verifyGiftPayment, {
      razorpay_order_id: paymentResponse.razorpay_order_id,
      razorpay_payment_id: paymentResponse.razorpay_payment_id,
      razorpay_signature: paymentResponse.razorpay_signature,
    });

    if (verificationResponse.error) {
      throw new Error(verificationResponse.error.message);
    }

    const verifiedPayload = verificationResponse.data;
    if (!verifiedPayload?.status || !verifiedPayload?.data?.message) {
      throw new Error(
        verifiedPayload?.message || "Gift payment could not be verified."
      );
    }

    onGiftSent(verifiedPayload.data.message);
    setOpen(false);
    setAmount(GIFT_PRESETS[0]);
    toast.success(`Gift of ₹${numericAmount} sent successfully`);
  };

  const startGiftPayment = async () => {
    if (!isValidAmount) {
      toast.error("Please select one of the available gift amounts.");
      return;
    }

    setIsProcessing(true);

    try {
      const sdkLoaded = await loadScript(
        "https://checkout.razorpay.com/v1/checkout.js"
      );

      if (!sdkLoaded) {
        throw new Error(
          "Razorpay could not be loaded. Please check your connection."
        );
      }

      const orderResponse: any = await APIRequest(PATH.createGiftOrder, {
        creatorId,
        amount: numericAmount,
        channelSlug,
      });

      if (orderResponse.error) {
        throw new Error(orderResponse.error.message);
      }

      const orderPayload = orderResponse.data;
      const order = orderPayload?.data?.order;

      if (!orderPayload?.status || !order?.order_id) {
        throw new Error(
          orderPayload?.message || "Gift order could not be created."
        );
      }

      const paymentObject = new (window as any).Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: Number(order.amount),
        currency: order.currency || "INR",
        name: "Fanxo Gift",
        description: `Send a gift to ${creatorName || "this creator"}`,
        order_id: order.order_id,
        handler: async (paymentResponse: any) => {
          try {
            await verifyAndSendGift(paymentResponse);
          } catch (error: any) {
            console.error("Gift verification failed", error);
            toast.error(
              error?.message || "Gift payment verification failed."
            );
          } finally {
            setIsProcessing(false);
          }
        },
        prefill: {
          name: "Fanxo",
          email: "fanxobusiness@gmail.com",
          contact: FANXOBILLPHONENUMBER,
        },
        notes: {
          creatorId,
          channelSlug,
          type: "gift",
        },
        theme: { color: "#0d6ac3" },
        modal: {
          ondismiss: () => setIsProcessing(false),
        },
      });

      paymentObject.on("payment.failed", (response: any) => {
        setIsProcessing(false);
        const reason = response?.error?.description || "Gift payment failed.";
        toast.error(reason);
      });

      paymentObject.open();
    } catch (error: any) {
      console.error("Unable to initiate gift", error);
      toast.error(error?.message || "Unable to initiate gift payment.");
      setIsProcessing(false);
    }
  };

  return (
    <>
      <GiftTrigger
        type="button"
        aria-label={`Send a gift to ${creatorName || "creator"}`}
        title="Send gift"
        onClick={() => setOpen(true)}
      >
        <FiGift size={20} />
      </GiftTrigger>

      {open && (
        <Overlay role="presentation" onMouseDown={closeModal}>
          <Dialog
            role="dialog"
            aria-modal="true"
            aria-labelledby="gift-dialog-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <DialogHeader>
              <div>
                <GiftBadge>
                  <FiGift size={22} />
                </GiftBadge>
                <h2 id="gift-dialog-title">Send a gift</h2>
                <p>Support {creatorName || "this creator"} directly in chat.</p>
              </div>
              <CloseButton
                type="button"
                onClick={closeModal}
                aria-label="Close"
              >
                <FiX size={20} />
              </CloseButton>
            </DialogHeader>

            <AmountLabel as="p">Gift amount</AmountLabel>
            <AmountInputWrapper aria-live="polite">
              <span>₹</span>
              <SelectedAmount>{amount}</SelectedAmount>
            </AmountInputWrapper>

            <PresetGrid>
              {GIFT_PRESETS.map((preset) => (
                <PresetButton
                  key={preset}
                  type="button"
                  data-selected={numericAmount === preset}
                  onClick={() => setAmount(preset)}
                  disabled={isProcessing}
                >
                  ₹{preset}
                </PresetButton>
              ))}
            </PresetGrid>

            <Hint $valid>Taxes included.</Hint>

            <PayButton
              type="button"
              onClick={startGiftPayment}
              disabled={!isValidAmount || isProcessing}
            >
              <FiGift size={18} />
              {isProcessing
                ? "Opening payment..."
                : `Pay ₹${numericAmount || 0} & send gift`}
            </PayButton>
          </Dialog>
        </Overlay>
      )}
    </>
  );
};

const GiftTrigger = styled.button`
  align-items: center;
  background: ${ctaGradient};
  border: 0;
  border-radius: 11px;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  min-width: 38px;
  padding: 0 10px;
  transition: opacity 140ms ease, transform 140ms ease;

  &:hover {
    opacity: 0.94;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const Overlay = styled.div`
  align-items: center;
  background: rgba(5, 7, 12, 0.72);
  backdrop-filter: blur(4px);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 20px;
  position: fixed;
  z-index: 10000;
`;

const Dialog = styled.div`
  background:
    linear-gradient(#0b0b0b, #0b0b0b) padding-box,
    ${ctaGradient} border-box;
  border: 2px solid transparent;
  border-radius: 22px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.62);
  color: #fff;
  padding: 24px;
  width: min(430px, calc(100vw - 32px));
`;

const DialogHeader = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: 22px;

  h2 {
    font-size: 22px;
    font-weight: 650;
    margin: 12px 0 5px;
  }

  p {
    color: rgba(255, 255, 255, 0.62);
    font-size: 14px;
    margin: 0;
  }
`;

const GiftBadge = styled.div`
  align-items: center;
  background: ${ctaGradient};
  border-radius: 12px;
  display: flex;
  height: 42px;
  justify-content: center;
  width: 42px;
`;

const CloseButton = styled.button`
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border: 0;
  border-radius: 10px;
  color: #fff;
  cursor: pointer;
  display: flex;
  height: 36px;
  justify-content: center;
  width: 36px;
`;

const AmountLabel = styled.label`
  color: rgba(255, 255, 255, 0.76);
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const AmountInputWrapper = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 13px;
  display: flex;
  font-size: 24px;
  font-weight: 650;
  padding: 0 16px;

  &:focus-within {
    border-color: rgba(200, 122, 227, 0.9);
  }
`;

const SelectedAmount = styled.output`
  flex: 1;
  padding: 14px 8px;
`;

const PresetGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin-top: 12px;

  @media (max-width: 420px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const PresetButton = styled.button`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.82);
  cursor: pointer;
  font-size: 12px;
  padding: 9px 4px;

  &[data-selected="true"] {
    background: ${ctaGradient};
    border-color: transparent;
    color: #fff;
  }
`;

const Hint = styled.p<{ $valid: boolean }>`
  color: ${({ $valid }) =>
    $valid ? "rgba(255, 255, 255, 0.62)" : "#ff9b9b"};
  font-size: 12px;
  line-height: 1.45;
  margin: 14px 0 18px;
`;

const PayButton = styled.button`
  align-items: center;
  background: ${ctaGradient};
  border: 0;
  border-radius: 12px;
  color: #fff;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 650;
  gap: 8px;
  justify-content: center;
  min-height: 44px;
  width: 100%;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export default GiftButton;
